import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StarRating } from "@/components/shared/Common";
import MatchScoreCard from "@/components/shared/MatchScoreCard";
import AvailabilityCalendar from "@/components/shared/AvailabilityCalendar";
import PublicReferences from "@/components/shared/PublicReferences";
import { COMPANY } from "@/constants/testIds";
import { Lock, ShieldCheck, MapPin, Briefcase, Award, Star, ClipboardCheck, TrendingUp } from "lucide-react";

export default function CompanyProfileDetail() {
  const { ssId } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [b, setB] = useState({ workers: 1, hours: 8, hourly_rate: 20, notes: "", date: "" });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opsOption, setOpsOption] = useState("none"); // none | ops_room | managed
  const [managedNotes, setManagedNotes] = useState("");
  const [opsPricing, setOpsPricing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [trust, setTrust] = useState({ partners: [], total_partners: 0 });

  useEffect(() => {
    api.get("/settings/ops-room-pricing").then((r) => setOpsPricing(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (p?.id) {
      api.get(`/reviews/for/${p.id}`).then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
    }
    if (p?.secusource_id) {
      api.get(`/public/professionals/${p.secusource_id}/trust-partners`)
        .then((r) => setTrust(r.data || { partners: [], total_partners: 0 }))
        .catch(() => setTrust({ partners: [], total_partners: 0 }));
    }
  }, [p?.id, p?.secusource_id]);

  useEffect(() => {
    const params = new URLSearchParams();
    ["role", "job_county", "required_kit", "required_qualifications"].forEach((k) => {
      const v = sp.get(k);
      if (v) params.set(k, v);
    });
    api.get(`/public/professionals/${ssId}${params.toString() ? "?" + params.toString() : ""}`)
      .then((r) => setP(r.data))
      .catch(() => toast.error("Profile not found"));
  }, [ssId, sp]);

  const getQuote = async () => {
    try {
      const { data } = await api.post("/bookings/quote", {
        target_type: "professional", target_id: p.id, ...b,
      });
      setQuote(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestBooking = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { target_type: "professional", target_id: p.id, ops_room_option: opsOption, managed_notes: managedNotes, ...b });
      const suffix = opsOption === "ops_room" ? " (Operations Room included)"
        : opsOption === "managed" ? " · Managed Deployment quote requested — SecuSource will email you." : "";
      toast.success("Booking created. Proceed to payment to unlock contact." + suffix);
      nav(`/company/bookings/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!p) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="card-flat p-6">
        <div className="flex flex-wrap justify-between gap-3 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="ss-id text-base">{p.secusource_id}</span>
              {p.verified && <span className="verified-chip"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>}
            </div>
            <div className="lock-chip"><Lock className="w-3.5 h-3.5" /> Identity locked until payment</div>
            <div className="mt-3 text-sm text-white/70 font-manrope flex flex-wrap gap-4">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.county}</span>
              <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {p.years_experience} yr exp</span>
            </div>
          </div>
          <StarRating rating={p.rating || 0} count={p.review_count || p.endorsements_count || 0} />
        </div>

        {/* Track record — Star rating × Reviews × Completed jobs */}
        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="pro-track-record">
          <div className="rounded-md border border-white/10 bg-[#0E0E0E] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Star rating</div>
            <div className="flex items-baseline justify-center gap-1 mt-1">
              <span className="text-2xl font-outfit font-bold text-white">{Number(p.rating || 0).toFixed(2)}</span>
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            </div>
            <div className="text-[11px] text-white/60 font-manrope">across {p.review_count || 0} review{(p.review_count || 0) === 1 ? "" : "s"}</div>
          </div>
          <div className="rounded-md border border-white/10 bg-[#0E0E0E] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Jobs completed</div>
            <div className="flex items-baseline justify-center gap-1 mt-1">
              <span className="text-2xl font-outfit font-bold text-white">{p.completed_jobs || 0}</span>
              <ClipboardCheck className="w-4 h-4 text-gold-500" />
            </div>
            <div className="text-[11px] text-white/60 font-manrope">verified through SecuSource</div>
          </div>
          <div className="rounded-md border border-white/10 bg-[#0E0E0E] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Reliability</div>
            <div className="flex items-baseline justify-center gap-1 mt-1">
              <span className="text-2xl font-outfit font-bold text-white">{Number((p.review_averages || {}).reliability || p.rating || 0).toFixed(1)}</span>
              <TrendingUp className="w-4 h-4 text-brand" />
            </div>
            <div className="text-[11px] text-white/60 font-manrope">from client feedback</div>
          </div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Roles</div>
            <div className="flex flex-wrap gap-1">{(p.role_categories || []).map((r) => <span key={r} className="chip">{r}</span>)}</div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mt-4 mb-2">Languages</div>
            <div className="flex flex-wrap gap-1">{(p.languages || []).map((l) => <span key={l} className="chip">{l}</span>)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Kit ({(p.kit || []).length})</div>
            <div className="flex flex-wrap gap-1 max-h-40 overflow-auto">{(p.kit || []).map((k) => <span key={k} className="chip"><Award className="w-3 h-3" />{k}</span>)}</div>
          </div>
        </div>

        {p.match && (
          <div className="mt-6">
            <MatchScoreCard match={p.match} size="md" showBreakdown={true} />
          </div>
        )}
      </div>

      {/* Availability calendar (read-only for companies) */}
      <AvailabilityCalendar userId={p.id} testIdPrefix="pro-avail" />

      {/* Trust graph — Companies who've worked with this pro */}
      {trust.partners.length > 0 && (
        <div className="card-flat p-6" data-testid="pro-trust-partners">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Companies who&apos;ve worked with this pro
              </div>
              <div className="font-outfit font-semibold text-white mt-1">
                Trusted by <span className="text-brand">{trust.total_partners}</span> verified compan{trust.total_partners === 1 ? "y" : "ies"} on SecuSource
              </div>
            </div>
            <span className="text-[11px] text-white/60 font-manrope inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Identities anonymised
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {trust.partners.slice(0, 6).map((tp) => (
              <div key={tp.author_ss_id} className="rounded-full bg-[#0E0E0E] border border-white/10 px-3 py-1.5 text-xs font-manrope flex items-center gap-2" data-testid={`trust-${tp.author_ss_id}`}>
                <span className="font-outfit font-bold text-white">{tp.author_ss_id}</span>
                <span className="text-white/50">·</span>
                <span className="text-white/85">{tp.jobs_together} job{tp.jobs_together === 1 ? "" : "s"}</span>
                {tp.avg_rating > 0 && (
                  <>
                    <span className="text-white/50">·</span>
                    <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {tp.avg_rating.toFixed(1)}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
          {trust.total_partners > 6 && (
            <div className="mt-2 text-[11px] text-white/60 font-manrope">Showing 6 of {trust.total_partners} company partners.</div>
          )}
        </div>
      )}

      {/* Recent client reviews */}
      {reviews.length > 0 && (
        <div className="card-flat p-6" data-testid="pro-reviews">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> Client reviews
              </div>
              <div className="font-outfit font-semibold text-white mt-1">
                Rated <span className="text-brand">{Number(p.rating || 0).toFixed(2)}/5</span> across {reviews.length} completed job{reviews.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {reviews.slice(0, 5).map((r) => (
              <div key={r.id} className="rounded-md border border-white/10 bg-[#0E0E0E] p-3" data-testid={`pro-review-${r.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(Number(r.overall_performance || 0)) ? "fill-amber-400" : "opacity-30"}`} />
                    ))}
                    <span className="text-xs text-white/60 ml-1 font-manrope">{Number(r.overall_performance || 0)}/5 overall</span>
                  </div>
                  <div className="text-[11px] text-white/60 font-manrope">
                    {new Date(r.created_at).toLocaleDateString()} · from {r.author_name || "verified company"}
                  </div>
                </div>
                {r.written && <div className="text-sm text-white font-manrope whitespace-pre-wrap">&ldquo;{r.written}&rdquo;</div>}
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-white/60 font-manrope">
                  <div>Reliability: <b className="text-white">{r.reliability}/5</b></div>
                  <div>Punctuality: <b className="text-white">{r.punctuality}/5</b></div>
                  <div>Communication: <b className="text-white">{r.communication}/5</b></div>
                  <div>Professionalism: <b className="text-white">{r.professionalism}/5</b></div>
                  <div>Appearance: <b className="text-white">{r.appearance}/5</b></div>
                  <div>Teamwork: <b className="text-white">{r.teamwork}/5</b></div>
                </div>
              </div>
            ))}
            {reviews.length > 5 && (
              <div className="text-xs text-white/60 font-manrope text-center">Showing 5 of {reviews.length} reviews</div>
            )}
          </div>
        </div>
      )}

      {/* Confirmed employer references (public backlinks) */}
      <PublicReferences userId={p.id} />

      {/* Booking flow */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-outfit font-semibold text-white">Request Booking</h2>
          <Button
            onClick={async () => {
              try {
                const { data } = await api.post("/negotiations", { professional_id: p.id, subject: `Negotiation with ${p.secusource_id}` });
                nav(`/negotiations/${data.id}`);
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }}
            variant="outline"
            className="border-brand text-brand hover:bg-brand-light"
            data-testid="open-negotiation-btn"
          >
            <Lock className="w-4 h-4 mr-2" /> Open Secure Negotiation Room
          </Button>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div><Label>Workers</Label><Input type="number" min={1} value={b.workers} onChange={(e) => setB({ ...b, workers: Number(e.target.value) })} data-testid="booking-workers" /></div>
          <div><Label>Hours</Label><Input type="number" min={1} step="0.5" value={b.hours} onChange={(e) => setB({ ...b, hours: Number(e.target.value) })} data-testid="booking-hours" /></div>
          <div><Label>Hourly rate (€)</Label><Input type="number" min={1} step="0.5" value={b.hourly_rate} onChange={(e) => setB({ ...b, hourly_rate: Number(e.target.value) })} data-testid="booking-rate" /></div>
          <div><Label>Date</Label><Input type="date" value={b.date} onChange={(e) => setB({ ...b, date: e.target.value })} data-testid="booking-date" /></div>
        </div>
        <div className="mt-4"><Label>Notes / Site instructions</Label><Textarea rows={3} value={b.notes} onChange={(e) => setB({ ...b, notes: e.target.value })} data-testid="booking-notes" /></div>

        <div className="mt-6 flex gap-3">
          <Button onClick={getQuote} variant="outline" className="border-brand text-brand hover:bg-brand-light" data-testid="booking-quote-btn">Get Fee Quote</Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button onClick={getQuote} className="bg-brand hover:bg-brand-hover text-white" data-testid={COMPANY.requestBooking}>Request Booking</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Confirm Booking Request</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm font-manrope text-white/85">
                <p>By confirming, you request a booking with <span className="ss-id">{p.secusource_id}</span>.</p>
                {quote && (
                  <div className="bg-navy-900 text-white p-4 rounded">
                    <div className="text-xs uppercase tracking-widest text-white/50 font-outfit">Booking value</div>
                    <div className="text-2xl font-outfit font-bold">€{quote.booking_value}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="text-xs">€1/hr fee: <strong>€{quote.per_hour_fee}</strong></div>
                      <div className="text-xs">3% fee: <strong>€{quote.percentage_fee}</strong></div>
                    </div>
                    <div className="mt-3 text-xs uppercase tracking-widest text-brand font-outfit font-bold">Applied Fee</div>
                    <div className="text-3xl font-outfit font-bold">€{quote.applied_fee}</div>
                    <div className="text-xs text-white/50 font-manrope">({quote.fee_method})</div>
                    {quote.credit_applied > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="text-xs text-gold-400 font-manrope">Referral credit applied: −€{quote.credit_applied}</div>
                        <div className="text-lg font-outfit font-bold text-gold-400">You pay €{quote.net_fee}</div>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-white/60">Contact details unlock after your payment is confirmed.</p>

                {/* Operations Room / Managed Deployment selector — Phase 1 */}
                <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid="ops-room-selector">
                  <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Deployment options</div>
                  <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${opsOption === "none" ? "border-brand bg-brand-light/50" : "border-white/10 hover:border-brand/50"}`} data-testid="opt-none">
                    <input type="radio" name="ops" checked={opsOption === "none"} onChange={() => setOpsOption("none")} className="mt-1" />
                    <div>
                      <div className="font-outfit font-semibold text-white">Standard Booking <span className="text-xs text-gold-500 font-manrope font-normal">· Included</span></div>
                      <div className="text-xs text-white/60 font-manrope">Unlock contacts, arrange the deployment yourself.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${opsOption === "ops_room" ? "border-brand bg-brand-light/50" : "border-white/10 hover:border-brand/50"}`} data-testid="opt-ops-room">
                    <input type="radio" name="ops" checked={opsOption === "ops_room"} onChange={() => setOpsOption("ops_room")} className="mt-1" />
                    <div>
                      <div className="font-outfit font-semibold text-white">Operations Room <span className="text-xs text-brand font-manrope font-normal">· +€{opsPricing?.ops_room_price ?? 29}</span></div>
                      <div className="text-xs text-white/60 font-manrope">Video briefing, document centre, SOP acknowledgement, attendance register, auto-generated compliance PDF. Recommended for events/festivals/VIP.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${opsOption === "managed" ? "border-gold-500 bg-gold-50" : "border-white/10 hover:border-gold-400"}`} data-testid="opt-managed">
                    <input type="radio" name="ops" checked={opsOption === "managed"} onChange={() => setOpsOption("managed")} className="mt-1" />
                    <div>
                      <div className="font-outfit font-semibold text-white">Managed Deployment <span className="text-xs text-gold-600 font-manrope font-normal">· Request a quote</span></div>
                      <div className="text-xs text-white/60 font-manrope">Everything in Operations Room + SecuSource Operations Coordinator hosts the meeting. Small €{opsPricing?.managed_small ?? 50} · Medium €{opsPricing?.managed_medium_min ?? 75}–€{opsPricing?.managed_medium_max ?? 125} · Large €{opsPricing?.managed_large_min ?? 150}–€{opsPricing?.managed_large_max ?? 250}+</div>
                    </div>
                  </label>
                  {opsOption === "managed" && (
                    <Textarea value={managedNotes} onChange={(e) => setManagedNotes(e.target.value)} placeholder="Tell us about the deployment — team size, event type, dates, any special requirements. We'll email you a quote." rows={3} data-testid="managed-notes" />
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={requestBooking} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid="booking-confirm-btn">
                  {busy ? "Creating…" : "Create Booking"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
